// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@stanion/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import systemColors from "@stanion/XDL/PreStyle/colors";
import { color, getAllColorsAsDropdownOptions, xScreen } from "@stanion/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        customCSS: string;
        // XNG-Changes:
        slideType: "fullWidth" | "half";
        firstButtonLabel: string;
        buttonLink: LinkFieldValue;
        firstButtonVariant: "primary" | "secondary" | "tertiary";
        firstButtonOpenInNewTab: boolean;
        secondButtonLabel: string;
        secondButtonLink: LinkFieldValue;
        secondButtonVariant: "primary" | "secondary" | "tertiary";
        secondButtonOpenInNewTab: boolean;
        smallImage: string;
        mediumImage: string;
        largeImage: string;
        altText: string;
        imageOverlay: string;
        overlay: boolean;
        overlayOpacity: string;
        overlayPosition: string;
        contentPosition: string;
        contentBackgroundColor: string;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    animationSpeed = "animationSpeed",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        // XNG-Changes:
        [fields.animationSpeed]: number;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    // XNG-Changes:
    buttonWrapper?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    buttonWrapper: {
        css: css`
            display: flex;
            gap: 32px;
            margin: 28px 0 32px 0;
            position: relative;
            z-index: 2;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                gap: 16px;
                align-items: flex-start;
            }
        `,
    },
    headingText: {
        css: css`
            p {
                font-size: 38px;
                font-weight: 900;
                line-height: 46px;
                ${xScreen("lte", "sm")} {
                    font-size: 32px;
                }
            }
        `,
    },
    subheadingText: {
        variant: "h5",
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    // XNG-Changes:
    const firstButtonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const secondButtonLinks = useGetLinks(fields.slides, o => o.fields.secondButtonLink);
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);

    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;
    const customCssWrapper = {
        css: css`
            .xng-pagination-container {
                display: flex;
                visibility: ${fields.slideIndicator ? "visible" : "hidden"};
                z-index: 2;
                bottom: 44px;
                ${xScreen("gt", "md")} {
                    bottom: 45px;
                }
                & > div,
                & > button {
                    padding: 0;
                }
            }

            .xng-pagination-container .arrow-button {
                visibility: ${fields.showArrows ? "visible" : "hidden"};
            }
            ${fields.customCSS}
        `,
    };

    // XNG-Changes: Update the styles components
    return (
        <StyledWrapper {...customCssWrapper}>
            <XngCarousel
                autoplay={{ delay: fields.autoplay }}
                speed={fields.animationSpeed}
                addStyledPaginationBlock={true}
            >
                {fields.slides.map((slide, index) => {
                    const firstButtonLink = firstButtonLinks[index];
                    const secondButtonLink = secondButtonLinks[index];

                    let backgroundImageStyles;
                    if (slide.fields.background === "image") {
                        if (isMobileScreen) {
                            backgroundImageStyles = `background-image: url(${
                                parseImageUrl(slide.fields.smallImage).url
                            });`;
                        } else if (isTabletScreen) {
                            backgroundImageStyles = `background-image: url(${
                                parseImageUrl(slide.fields.mediumImage).url
                            });`;
                        } else {
                            backgroundImageStyles = `background-image: url(${
                                parseImageUrl(slide.fields.largeImage).url
                            });`;
                        }
                    } else {
                        backgroundImageStyles = `background-color: ${slide.fields.backgroundColor};`;
                    }

                    const backgroundStyles = `
                        ${backgroundImageStyles}
                        background-repeat: no-repeat;
                        background-position: center center;
                        background-size: cover;
                    `;

                    let fontSizeStyles;
                    if (fields.responsiveFontSizes || fields.customFontSizes) {
                        fontSizeStyles = responsiveStyleRules(
                            fields.responsiveFontSizes,
                            fields.customFontSizes ? fields : undefined,
                        );
                    }

                    const fullImageSlideWrapperStyles = {
                        css: css`
                            ${styles.slideContentWrapper?.css || ""}
                            ${textAlignStyles}
                            ${backgroundStyles}
                            ${fontSizeStyles}
                            position: relative;
                            min-height: 450px;
                            overflow: hidden;
                        `,
                    };

                    const halfImagebackground = {
                        css: css`
                            min-width: 50%;
                            min-height: 450px;
                            ${backgroundStyles}
                        `,
                    };

                    const tabletImageBackground = {
                        css: css`
                            width: 100%;
                            ${backgroundStyles}
                            height: 250px;
                            max-height: 250px;
                            ${xScreen("lte", "xs")} {
                                height: 120px;
                                max-height: 120px;
                            }
                        `,
                    };

                    const overlayWrapperStyles = {
                        css: css`
                            background-color: ${slide.fields.background === "image" ? slide.fields.imageOverlay : ""};
                            padding: ${slide.fields.contentPadding}px;
                            opacity: ${slide.fields.overlayOpacity};
                            position: absolute;
                            top: 0px;
                            height: 100%;
                            z-index: 1;
                            width: 47%;
                            ${xScreen("lt", "xl")} {
                                width: 57%;
                            }
                            ${slide.fields.overlayPosition === "right"
                                ? css`
                                      clip-path: polygon(40% 0, 100% 0, 100% 100%, 0% 100%);
                                      right: 0px;
                                  `
                                : css`
                                      clip-path: polygon(0 0, 65% 0, 100% 100%, 0% 100%);
                                      left: 0px;
                                  `}
                        `,
                    };

                    const fullImageContentWrapperStyles = {
                        css: css`
                            padding: ${slide.fields.contentPadding}px;
                            max-width: 490px;
                            z-index: 2;
                            position: relative;
                        `,
                    };

                    const halfImageSlideWrapperStyles = {
                        css: css`
                            display: flex;
                            height: 100%;
                            background-color: ${slide.fields.contentBackgroundColor};
                        `,
                    };

                    const halfImageContentWrapperStyles = {
                        css: css`
                            ${textAlignStyles}
                            padding: 0 25px;
                            padding-top: ${slide.fields.contentPadding}px;
                            max-width: 570px;
                        `,
                    };

                    const tabletSlideWrapperStyles = {
                        css: css`
                            height: 100%;
                            background-color: ${slide.fields.contentBackgroundColor};
                        `,
                    };

                    const tabletContentWrapperStyles = {
                        css: css`
                            padding: 20px 16px;
                            max-width: 640px;
                            ${xScreen("lte", "sm")} {
                                max-width: 100%;
                            }
                        `,
                    };

                    const contentPosition = {
                        css: css`
                            display: flex;
                            justify-content: ${slide.fields.contentPosition};
                        `,
                    };

                    const slideContent = (
                        <>
                            {slide.fields.heading && (
                                <Typography className="slide-heading" {...styles.headingText}>
                                    {parse(slide.fields.heading, parserOptions)}
                                </Typography>
                            )}
                            {slide.fields.subheading && (
                                <Typography className="slide-subheading" {...styles.subheadingText}>
                                    {parse(slide.fields.subheading, parserOptions)}
                                </Typography>
                            )}
                            <StyledWrapper {...styles.buttonWrapper}>
                                {(slide.fields.firstButtonLabel || slide.fields.buttonLink.value) && (
                                    <Link
                                        href={firstButtonLink?.url}
                                        target={slide.fields.firstButtonOpenInNewTab ? "_blank" : ""}
                                    >
                                        <Button
                                            className="slide-button"
                                            {...styles.slideButton}
                                            variant={slide.fields.firstButtonVariant}
                                        >
                                            {slide.fields.firstButtonLabel}
                                        </Button>
                                    </Link>
                                )}
                                {(slide.fields.secondButtonLabel || slide.fields.secondButtonLink.value) && (
                                    <Link
                                        href={secondButtonLink?.url}
                                        target={slide.fields.secondButtonOpenInNewTab ? "_blank" : ""}
                                    >
                                        <Button
                                            className="slide-button"
                                            {...styles.slideButton}
                                            variant={slide.fields.secondButtonVariant}
                                        >
                                            {slide.fields.secondButtonLabel}
                                        </Button>
                                    </Link>
                                )}
                            </StyledWrapper>
                        </>
                    );

                    // XNG-Changes: Update the structure
                    return (
                        <>
                            {isTabletScreen ? (
                                <StyledWrapper key={slide.fields.slideTitle} {...tabletSlideWrapperStyles}>
                                    <StyledWrapper {...tabletImageBackground}></StyledWrapper>
                                    <StyledWrapper {...tabletContentWrapperStyles}>{slideContent}</StyledWrapper>
                                </StyledWrapper>
                            ) : slide.fields.slideType === "fullWidth" ? (
                                <StyledWrapper key={slide.fields.slideTitle} {...fullImageSlideWrapperStyles}>
                                    {slide.fields.overlay && <StyledWrapper {...overlayWrapperStyles}></StyledWrapper>}
                                    <StyledWrapper {...contentPosition}>
                                        <StyledWrapper {...fullImageContentWrapperStyles}>{slideContent}</StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ) : (
                                <StyledWrapper key={slide.fields.slideTitle} {...halfImageSlideWrapperStyles}>
                                    <StyledWrapper {...halfImagebackground}></StyledWrapper>
                                    <StyledWrapper {...halfImageContentWrapperStyles}>{slideContent}</StyledWrapper>
                                </StyledWrapper>
                            )}
                        </>
                    );
                })}
            </XngCarousel>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
                isVisible: item => false,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10000,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                isRequired: true,
            },
            {
                name: fields.animationSpeed,
                editorTemplate: "IntegerField",
                defaultValue: 500,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                isRequired: true,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "slideType",
                        displayName: "Slide Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Full width", value: "fullWidth" },
                            { displayName: "Half", value: "half" },
                        ],
                        defaultValue: "fullWidth",
                        isRequired: true,
                        hideEmptyOption: true,
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "smallImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: true,
                    },
                    {
                        name: "mediumImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: true,
                    },
                    {
                        name: "largeImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: true,
                    },
                    {
                        name: "altText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "Image Alt Text",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: true,
                    },
                    {
                        name: "overlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.slideType === "fullWidth",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Overlay Color",
                        editorTemplate: "DropDownField",
                        options: [
                            ...getAllColorsAsDropdownOptions(systemColors),
                            {
                                displayName: "Custom color",
                                value: "customColor",
                            },
                        ],
                        defaultValue: "",
                        isVisible: widget => widget.fields.overlay && widget.fields.slideType === "fullWidth",
                    },
                    {
                        name: "overlayPosition",
                        displayName: "Overlay Position",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "right",
                        hideEmptyOption: true,
                        isVisible: widget => widget.fields.overlay && widget.fields.slideType === "fullWidth",
                    },
                    {
                        name: "overlayOpacity",
                        displayName: "Overlay Opacity (0.1 - 1)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.overlay && widget.fields.slideType === "fullWidth",
                    },
                    {
                        name: "contentPosition",
                        displayName: "Content Position",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                        defaultValue: "flex-start",
                        isVisible: widget => widget.fields.overlay && widget.fields.slideType === "fullWidth",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "contentBackgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Content Background Color",
                        defaultValue: "white",
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "firstButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "First Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "First Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "firstButtonVariant",
                        displayName: "First Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "firstButtonOpenInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "secondButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "Second Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "secondButtonLink",
                        displayName: "Second Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "secondButtonVariant",
                        displayName: "Second Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "secondButtonOpenInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
